import { createApi } from '@reduxjs/toolkit/query/react';
import { type Answer, type Option, type Question } from '@strata/react-question-tree';

import { EPA_API_URL, fmhAPI } from 'api/APIBase';
import {
    type Determination,
    type PriorAuthErrorCodes,
    type TransactionType,
} from 'services/constants';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery, jsonToFormData } from 'store/api/apiBaseQuery';

const TagType = {
    EPA_CASES: 'EPA_CASES',
} as const;

export interface RawAnswer {
    question_id: number | string;
    selected_options: Option[];
    additional_free_text?: string;
    is_invalid: boolean;
    not_validated?: boolean;
    next_question_id?: string;
}
interface EPaResponseItem {
    transaction_id: string;
    question_set?: {
        nodes: Question[];
        answers: RawAnswer[];
        is_complete?: boolean;
    };
    pa_note?: string;
    reason_code?: (typeof PriorAuthErrorCodes)[keyof typeof PriorAuthErrorCodes];
    error_response?: string;
    transaction_type: (typeof TransactionType)[keyof typeof TransactionType];
    determination: (typeof Determination)[keyof typeof Determination];
    pharmacy_type?: string;
    external_case_id?: string;
    due_date?: string;
    prior_auth_number?: string;
    number_of_cycles?: number;
    effective_date?: string;
    expiration_date?: string;
    is_complete?: boolean;
}

interface EPaCaseResponse {
    epa_case_id: string;
    epa_created_on: string;
    responses?: EPaResponseItem[];
}

export interface EPaResponse {
    transactionId: string;
    questionSet?: {
        nodes: Question[];
        answers: RawAnswer[];
    };
    paNote?: string;
    reasonCode?: (typeof PriorAuthErrorCodes)[keyof typeof PriorAuthErrorCodes];
    errorResponse?: string;
    transactionType: (typeof TransactionType)[keyof typeof TransactionType];
    determination: (typeof Determination)[keyof typeof Determination];
    pharmacyType?: string;
    externalCaseId?: string;
    dueDate?: string;
    priorAuthNumber?: string;
    numberOfCycles?: number;
    effectiveDate?: string;
    expirationDate?: string;
    /** Mirrors persisted question_set.is_complete when present. */
    isComplete?: boolean;
}

export interface EPaCase {
    epaCaseId: string;
    epaCreatedOn: string; // date the epa was created
    responses?: EPaResponse[];
}

interface AttachmentMetadataResponse {
    attachment_id: string;
    transaction_id: string;
    attachment_folder: string;
    attachment_filename: string;
    mime_type: string;
}

export interface AttachmentMetadata {
    attachmentId: string;
    transactionId: string;
    attachmentFolder: string;
    attachmentFilename: string;
    mimeType: string;
}

export const epaApi = createApi({
    baseQuery: getBaseQuery(EPA_API_URL),
    reducerPath: 'epaApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getEPaCases: build.query<EPaCase[], { intakeCaseId: string }>({
            query: ({ intakeCaseId }) => `/${intakeCaseId}`,
            transformResponse: (response: EPaCaseResponse[]) =>
                response?.map((epaCase) => ({
                    epaCaseId: epaCase.epa_case_id,
                    epaCreatedOn: epaCase.epa_created_on,
                    responses: epaCase.responses?.map((r) => ({
                        transactionId: r.transaction_id,
                        questionSet: r.question_set,
                        paNote: r.pa_note,
                        reasonCode: r.reason_code,
                        errorResponse: r.error_response,
                        transactionType: r.transaction_type,
                        determination: r.determination,
                        pharmacyType: r.pharmacy_type,
                        externalCaseId: r.external_case_id,
                        dueDate: r.due_date,
                        priorAuthNumber: r.prior_auth_number,
                        numberOfCycles: r.number_of_cycles,
                        effectiveDate: r.effective_date,
                        expirationDate: r.expiration_date,
                        isComplete: r.is_complete,
                    })),
                })),
            transformErrorResponse: handleRTKQueryError('Error fetching EPA cases.'),
            providesTags: (_result, _error, { intakeCaseId }) => [
                { type: TagType.EPA_CASES, id: intakeCaseId },
            ],
        }),
        postEpaCase: build.mutation<
            void,
            {
                transactionId: string;
                nodes: Question[];
                answers: Answer[];
                isComplete?: boolean;
                intakeCaseId: string;
                attachment?: File[];
            }
        >({
            query: ({ transactionId, nodes, answers, isComplete, attachment }) => ({
                url: `/request/${transactionId}`,
                method: 'POST',
                body: jsonToFormData({
                    nodes: JSON.stringify(nodes),
                    answers: JSON.stringify(
                        answers.map((answer) => ({
                            ...answer,
                            selected_options: answer.selectedOptions,
                            next_question_id: answer.nextQuestionId,
                        })),
                    ),
                    is_complete: isComplete,
                    attachment,
                }),
            }),
            transformErrorResponse: handleRTKQueryError('Error saving EPA case.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error ? [TagType.EPA_CASES, { type: TagType.EPA_CASES, id: intakeCaseId }] : [],
        }),
        postCancelEpaCase: build.mutation<void, { epaCaseId: string; intakeCaseId: string }>({
            query: ({ epaCaseId }) => ({
                url: `/cancel/${epaCaseId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError('Error canceling EPA case.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error ? [TagType.EPA_CASES, { type: TagType.EPA_CASES, id: intakeCaseId }] : [],
        }),
        getAttachments: build.query<AttachmentMetadata[], { transactionId: string }>({
            query: ({ transactionId }) => `/attachment/${transactionId}`,
            transformResponse: (response: AttachmentMetadataResponse[]) =>
                response?.map((metadata) => ({
                    attachmentId: metadata.attachment_id,
                    transactionId: metadata.transaction_id,
                    attachmentFolder: metadata.attachment_folder,
                    attachmentFilename: metadata.attachment_filename,
                    mimeType: metadata.mime_type,
                })),
            transformErrorResponse: handleRTKQueryError('Error fetching attachments.'),
        }),
    }),
});

export async function getAttachmentBlob(
    transactionId: string,
    attachment_id: string,
    filename: string,
) {
    const url = `${EPA_API_URL}/attachment/${transactionId}/${attachment_id}`;
    return fmhAPI.base
        .url(url)
        .get()
        .blob((blob) => new File([blob], filename, { type: blob.type }));
}

export const {
    useGetEPaCasesQuery,
    usePostEpaCaseMutation,
    usePostCancelEpaCaseMutation,
    useGetAttachmentsQuery,
} = epaApi;
